import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const StartupProfileEditPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    jobTitle: '',
    products: [],
    technologies: [],
    patents: [],
    teamMembers: [],
    funding: [],
    spending: []
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  useEffect(() => {
    const fetchStartupProfile = async () => {
      try {
        setLoading(true);
        
        // Get user profile
        const { data: userProfile, error: userProfileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (userProfileError) throw userProfileError;
        
        // Get products
        const { data: products, error: productsError } = await supabase
          .from('products')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (productsError) throw productsError;
        
        // Get technologies
        const { data: technologies, error: techError } = await supabase
          .from('technology_platforms')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (techError) throw techError;
        
        // Get patents
        const { data: patents, error: patentsError } = await supabase
          .from('patents')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (patentsError) throw patentsError;
        
        // Get team members
        const { data: teamMembers, error: teamError } = await supabase
          .from('team_members')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (teamError) throw teamError;
        
        // Get funding
        const { data: funding, error: fundingError } = await supabase
          .from('funding')
          .select('*')
          .eq('user_id', user.id)
          .order('date_received', { ascending: false });
          
        if (fundingError) throw fundingError;
        
        // Get spending
        const { data: spending, error: spendingError } = await supabase
          .from('spending')
          .select('*')
          .eq('user_id', user.id)
          .order('date_spent', { ascending: false });
          
        if (spendingError) throw spendingError;
        
        // Set form data
        setFormData({
          name: userProfile.name || '',
          company: userProfile.company || '',
          jobTitle: userProfile.job_title || '',
          products: products || [],
          technologies: technologies || [],
          patents: patents || [],
          teamMembers: teamMembers || [],
          funding: funding || [],
          spending: spending || []
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching startup profile:', err);
        setError('Failed to load profile. Please try again.');
        setLoading(false);
      }
    };
    
    if (user) {
      fetchStartupProfile();
    }
  }, [user, navigate]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    
    try {
      // Update user profile
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          name: formData.name,
          company: formData.company,
          job_title: formData.jobTitle
        })
        .eq('user_id', user.id);
      
      if (updateError) throw updateError;
      
      setSuccess('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating startup profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return <LoadingScreen message="Loading your startup profile..." />;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Startup Profile</h1>
        
        {error && (
          <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}
        
        {success && (
          <div className="p-4 mb-6 bg-green-100 text-green-700 rounded-md" role="alert">
            {success}
          </div>
        )}
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                Full Name*
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                Company Name*
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="jobTitle" className="block text-gray-700 font-medium mb-2">
                Job Title*
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            
            {/* Products Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Products ({formData.products.length})</h3>
              <div className="space-y-4">
                {formData.products.map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div>
                      <h4 className="font-medium text-gray-900">{product.name}</h4>
                      <p className="text-sm text-gray-600">
                        {product.development_stage} • {product.product_category}
                      </p>
                    </div>
                    <Link
                      to={`/products/${product.id}/edit`}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </Link>
                  </div>
                ))}
                <Link
                  to="/products/create"
                  className="btn-primary text-sm px-3 py-1 inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Product
                </Link>
              </div>
            </div>
            
            {/* Technologies Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Technologies ({formData.technologies.length})</h3>
              <div className="space-y-4">
                {formData.technologies.map((tech) => (
                  <div key={tech.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div>
                      <h4 className="font-medium text-gray-900">{tech.name}</h4>
                      <p className="text-sm text-gray-600">{tech.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/technologies/${tech.id}/edit`)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/technologies/create')}
                  className="btn-primary text-sm px-3 py-1 inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Technology
                </button>
              </div>
            </div>
            
            {/* Patents Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Patents ({formData.patents.length})</h3>
              <div className="space-y-4">
                {formData.patents.map((patent) => (
                  <div key={patent.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div>
                      <h4 className="font-medium text-gray-900">{patent.name}</h4>
                      <p className="text-sm text-gray-600">
                        {patent.patent_number && `Patent No: ${patent.patent_number}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/patents/${patent.id}/edit`)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/patents/create')}
                  className="btn-primary text-sm px-3 py-1 inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Patent
                </button>
              </div>
            </div>
            
            {/* Team Members Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members ({formData.teamMembers.length})</h3>
              <div className="space-y-4">
                {formData.teamMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div>
                      <h4 className="font-medium text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-600">
                        {member.title}
                        {member.credentials && ` • ${member.credentials}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/team/${member.id}/edit`)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/team/create')}
                  className="btn-primary text-sm px-3 py-1 inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Team Member
                </button>
              </div>
            </div>
            
            {/* Funding Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Funding ({formData.funding.length})</h3>
              <div className="space-y-4">
                {formData.funding.map((fund) => (
                  <div key={fund.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div>
                      <h4 className="font-medium text-gray-900">{fund.name}</h4>
                      <p className="text-sm text-gray-600">
                        {fund.source} • ${fund.amount?.toLocaleString()}
                        {fund.date_received && ` • ${new Date(fund.date_received).toLocaleDateString()}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/funding/${fund.id}/edit`)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/funding/create')}
                  className="btn-primary text-sm px-3 py-1 inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Funding
                </button>
              </div>
            </div>
            
            {/* Spending Section */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Spending ({formData.spending.length})</h3>
              <div className="space-y-4">
                {formData.spending.map((spend) => (
                  <div key={spend.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                    <div>
                      <h4 className="font-medium text-gray-900">{spend.item}</h4>
                      <p className="text-sm text-gray-600">
                        ${spend.amount?.toLocaleString()}
                        {spend.date_spent && ` • ${new Date(spend.date_spent).toLocaleDateString()}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/spending/${spend.id}/edit`)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/spending/create')}
                  className="btn-primary text-sm px-3 py-1 inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Spending
                </button>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StartupProfileEditPage; 